<script lang="ts">
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import type { Device } from 'src/types/Device';
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { inputStates } from 'src/stores/features/input-states';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { Text } from '@Shelly/shelly-design-system';
	import { timestampToDate } from 'src/helpers/date_helper';
	import { is_GBLE, shellyId16 } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { SENSOR_NO_REPORTS_MS, type SensorOverlay } from 'src/helpers/sensor_priority_layout';

	export let device: Device;
	export let overlay: SensorOverlay | null = null;

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: isBle = is_GBLE(device);
	$: showReporter = isBle && !device.shared;
	$: reporterId =
		$inputStates?.[device.id]?.reporter?.id ||
		(typeof status?.reporter?.id === 'string' ? status.reporter.id : undefined);
	$: reporter = reporterId && reporterId !== '' ? $devices[shellyId16(reporterId)] : undefined;

	$: updated = $inputStates?.[device.id]?.time ?? status?._updated;
	$: timeString = (() => {
		if (typeof updated !== 'number' || !status?._updated) return '';
		const elapsed = Date.now() - updated * 1000;
		if (elapsed < 60_000) {
			return capFirstChar(
				$format('span_seconds_ago', {
					default: '{time} seconds ago.',
					values: { time: Math.abs(Number((elapsed / 1000).toFixed(0))) },
				}),
			);
		}
		return timestampToDate(updated * 1000);
	})();
	$: reportStale =
		Boolean(overlay) ||
		(isBle && typeof updated === 'number' && Date.now() - updated * 1000 > SENSOR_NO_REPORTS_MS);
	$: showReport = Boolean(timeString) || showReporter;

	const onReporterClick = (event: MouseEvent) => {
		event.preventDefault();
		if (!reporter) return;
		push(`/home/room/${reporter.room_id}/devices`);
		setSidePanelState({ id: reporter.id });
	};
</script>

{#if showReport}
	<div
		class="sensor-report mt-m"
		role="group"
		aria-label={$format('label_sensor_report', { default: 'Last report and reporter' })}
	>
		{#if timeString}
			<div class="d-flex justify-content-between align-items-baseline gap-s">
				<Text styleType="miniature" class="text-color-muted">
					{capFirstChar($format('label_last_report', { default: 'last report' }))}
				</Text>
				<Text styleType="miniature" class={reportStale ? 'text-color-muted' : ''}>
					{timeString}
				</Text>
			</div>
		{/if}
		{#if showReporter}
			<div class="d-flex justify-content-between align-items-baseline gap-s">
				<Text styleType="miniature" class="text-color-muted">
					{$format('label_sensor_reporter', { default: 'Reporter' })}
				</Text>
				{#if reporter}
					<Text
						href="#"
						styleType="miniature"
						aria-label={$format('label_open_reporter', {
							default: 'Open {name}',
							values: { name: reporter.name },
						})}
						on:click={onReporterClick}
					>
						{reporter.name}
					</Text>
				{:else}
					<Text styleType="miniature" class="text-color-muted">
						{$format('option_none', { default: 'None' })}
					</Text>
				{/if}
			</div>
		{/if}
	</div>
{/if}

<style>
	.sensor-report {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		padding-top: var(--spacing-m);
		border-top: 1px solid var(--color-2);
	}
</style>
